import React, { useState, useEffect } from 'react';
import { X, Upload, Calendar } from 'lucide-react';
import { uploadImage } from '../../services/firebase-storage.js';

const EditModal = ({ item, contentType, config, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    // Initialize form data with item values
    setFormData({ ...item });
    setImagePreview(item.imageUrl || item.image_url || '');
  }, [item]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let updateData = formData;

      // Handle image upload if new image selected
      if (imageFile) {
        const folderMap = {
          products: 'products',
          services: 'services',
          references: 'references',
          news: 'news',
          gallery: 'gallery'
        };

        const imageUrl = await uploadImage(imageFile, folderMap[contentType]);
        updateData = {
          ...updateData,
          image_url: imageUrl,
          firebase_image_path: `${folderMap[contentType]}/${imageFile.name}`
        };
      }

      // Remove unnecessary fields
      const cleanData = { ...updateData };
      delete cleanData.id;
      delete cleanData.created_at;
      delete cleanData.updated_at;

      const updatedItem = await config.api.update(item.id, cleanData);
      onSuccess({ ...cleanData, id: item.id });
    } catch (error) {
      console.error('Error updating item:', error);
      onError('❌ Güncelleme sırasında hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (fieldName) => {
    const value = formData[fieldName] || '';

    switch (fieldName) {
      case 'title':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Başlık *
            </label>
            <input
              type="text"
              name="title"
              value={value}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        );

      case 'description':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {contentType === 'news' ? 'İçerik' : 'Açıklama'} *
            </label>
            <textarea
              name="description"
              value={value}
              onChange={handleInputChange}
              rows={contentType === 'news' ? 8 : 4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        );

      case 'alt_text':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Açıklama
            </label>
            <input
              type="text"
              name="alt_text"
              value={value}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Görsel açıklaması..."
            />
          </div>
        );

      case 'published_date':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yayın Tarihi *
            </label>
            <input
              type="date"
              name="published_date"
              value={value ? value.split('T')[0] : ''}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        );

      case 'category':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori
            </label>
            <select
              name="category"
              value={value}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="projeler">Projeler</option>
              <option value="makineler">Makineler</option>
              <option value="ekip">Ekip</option>
              <option value="genel">Genel</option>
            </select>
          </div>
        );

      case 'is_featured':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_featured"
              checked={formData.is_featured || false}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Öne çıkarılsın
            </label>
          </div>
        );

      case 'is_published':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_published"
              checked={formData.is_published !== false}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Yayında olsun
            </label>
          </div>
        );

      case 'is_active':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active !== false}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Aktif olsun
            </label>
          </div>
        );

      case 'image_url':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Görsel
            </label>
            
            {/* Current Image Preview */}
            {imagePreview && (
              <div className="mb-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-lg border"
                />
              </div>
            )}

            {/* File Input */}
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Yeni görsel yükle</span>
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG (MAX. 10MB)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {config.title} Düzenle
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {config.fields.map((field) => (
            <div key={field}>
              {renderField(field)}
            </div>
          ))}

          {/* Add visibility toggle fields based on content type */}
          {contentType === 'news' && (
            <div>
              {renderField('is_published')}
            </div>
          )}

          {(contentType === 'products' || contentType === 'services') && (
            <div>
              {renderField('is_active')}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Güncelleniyor...' : 'Güncelle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal; 